import React, { memo, useCallback, useContext, useMemo } from 'react';
import { useFeatureFlag } from '@olx/react-laquesis';
import DetailsCard from './DetailsCard';
import S from './style/DeliveryMethodCard';
import useI18n from '../../../../i18n/hooks/useI18n';
import TransactionContext from '../contexts/TransactionContext';
import TabsContext from '../contexts/TabsContext';
import { TAB_TYPE_BUYING } from '../../constants';

type PickUpWindow = { date: string; startTime: string; endTime: string };

function formatPickupWindow({ start, end }: { start?: string; end?: string }): PickUpWindow {
  const locale = 'pl-PL';
  const startDateObj = new Date(start);
  const endDateObj = new Date(end);
  const date = new Intl.DateTimeFormat(locale).format(startDateObj);

  const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric' };
  const startTime = new Intl.DateTimeFormat(locale, timeOptions).format(startDateObj);
  const endTime = new Intl.DateTimeFormat(locale, timeOptions).format(endDateObj);

  return { date, startTime, endTime };
}

const DeliveryMethodCard: React.FC<{
  children: React.ReactNode;
}> = function DeliveryMethodCard({ children }) {
  // TODO: remove feature flag OPIU-438.
  const isD2Denabled = useFeatureFlag('opiu-438');
  const transaction = useContext(TransactionContext);
  const tabType = useContext(TabsContext);
  const servicePoint =
    tabType === TAB_TYPE_BUYING ? transaction?.destinationPoint : transaction?.packingPoint;

  const operator = servicePoint?.id?.split(':')[0];
  const isInPost = servicePoint?.brand === 'inpost';
  const servicePointId = servicePoint?.id?.split(':')[1];
  const isLocker = isInPost && servicePointId?.indexOf('POP-') === -1;
  const inPostShipmentKey = isLocker ? 'inpost' : 'inpost_pop';
  let pickUpWindow: PickUpWindow;

  const i18n = useI18n();
  const i18nKey = useCallback(
    (key: string | TemplateStringsArray) => i18n(`dlv_orders_details_${key}`),
    [i18n],
  );

  const i18nSubtitleKey = useCallback(
    (key: string | TemplateStringsArray) => i18n(`dlv_order_card_desktop_${key}`),
    [i18n],
  );
  const subtitle = useMemo(() => {
    if (transaction?.deliveryType === 'D2D') {
      return i18nKey`door_to_door`;
    }

    return tabType === TAB_TYPE_BUYING ? i18nSubtitleKey`pickup` : i18nSubtitleKey`post`;
  }, [transaction, tabType]);

  const i18nShipmentKey = useCallback(
    (key: string | TemplateStringsArray) => i18n(`dlv_shipment_operator_${key}`),
    [i18n],
  );

  const title = isInPost
    ? `${i18nShipmentKey(inPostShipmentKey)} ${servicePointId}`
    : `${i18nShipmentKey(operator)} ${servicePointId}`;

  const servicePointInfo = useMemo(
    () => (
      <S.Wrapper>
        <S.Title>{servicePoint && title}</S.Title>
        <S.Description loadingWidth="30%">{servicePoint && servicePoint?.street}</S.Description>
        <S.Description loadingWidth="40%">
          {servicePoint && `${servicePoint?.postalCode} ${servicePoint?.city}`}
        </S.Description>
        {servicePoint?.description && (
          <S.Description loadingWidth="40%">{`[${servicePoint?.description}]`}</S.Description>
        )}
      </S.Wrapper>
    ),
    [servicePoint, title],
  );

  const doorToDoorInfo = useMemo(() => {
    if (tabType === TAB_TYPE_BUYING) {
      return (
        <S.Wrapper>
          <S.TitleWithMargin>{i18nKey`deliveryaddress`}</S.TitleWithMargin>
          <S.Description loadingWidth="30%">
            {transaction?.destinationAddress?.streetName}{' '}
            {transaction?.destinationAddress?.streetNumber}{' '}
            {transaction?.destinationAddress?.apartmentNumber}
          </S.Description>
          <S.Description loadingWidth="30%">
            {transaction?.destinationAddress?.postCode} {transaction?.destinationAddress?.cityName}
          </S.Description>
        </S.Wrapper>
      );
    }

    if (transaction?.pickUpWindow?.start && transaction?.pickUpWindow?.end) {
      pickUpWindow = formatPickupWindow(transaction?.pickUpWindow);
    }

    const { date, startTime, endTime } = pickUpWindow || {};
    return (
      <S.Wrapper>
        <S.TitleWithMargin>{i18nKey`pickupaddress`}</S.TitleWithMargin>
        <S.Description loadingWidth="30%">
          {transaction?.packingAddress?.streetName} {transaction?.packingAddress?.streetNumber}{' '}
          {transaction?.packingAddress?.apartmentNumber}
        </S.Description>
        <S.Description loadingWidth="30%">
          {transaction?.packingAddress?.postCode} {transaction?.packingAddress?.cityName}
        </S.Description>

        {date && startTime && endTime ? (
          <>
            <S.TitleWithMargin>{i18nKey`pickupdate`}</S.TitleWithMargin>
            <S.Description loadingWidth="30%">
              {date}, {startTime} - {endTime}
            </S.Description>
          </>
        ) : null}
      </S.Wrapper>
    );
  }, []);

  return (
    <DetailsCard data-cy="delivery-method-info" title={i18nKey`deliverymethod`} subtitle={subtitle}>
      {transaction?.deliveryType === 'D2D' && isD2Denabled ? doorToDoorInfo : servicePointInfo}
      {children}
    </DetailsCard>
  );
};

export default memo(DeliveryMethodCard);
